import { useNavigate } from 'react-router-dom'
import type { RosBuyerRow, RosDashboardFilters } from './types'
import { useCanOpenQueue } from '../../procurement/queueAccess'
import { queueLink } from './useRosDashboard'

// Таблица «По закупщикам» — ядро дашборда РОЗ. Столбцы: Закупщик · В работе ·
// Проквот. · Без ответа · Отказ (цена 0) · В счёт. Колонка «Новые» убрана
// (решение 16.07: назначение позиций закупщикам не используется — были нули).
// Любая числовая ячейка кликабельна → очередь по этому закупщику и метрике
// (композитные серверные фильтры единой «эффективной» шкалы). У ADMIN очередь
// закрыта (18.09) — ячейки у него без клика (решение Сергея 25.09).

type MetricKey = keyof Pick<
  RosBuyerRow,
  'inWork' | 'quoted' | 'noAnswer' | 'zeroPrice' | 'toInvoice'
>

const COLUMNS: { key: MetricKey; label: string; queueParams: Record<string, string> }[] = [
  { key: 'inWork', label: 'В работе', queueParams: { inWork: '1' } },
  { key: 'quoted', label: 'Проквот.', queueParams: { procState: 'QUOTED' } },
  { key: 'noAnswer', label: 'Без ответа', queueParams: { noAnswer: '1' } },
  { key: 'zeroPrice', label: 'Отказ (цена 0)', queueParams: { zeroPrice: '1' } },
  { key: 'toInvoice', label: 'В счёт', queueParams: { wentToInvoice: '1' } },
]

export function RosBuyersTable({
  rows,
  filters,
}: {
  rows: RosBuyerRow[]
  filters: RosDashboardFilters
}) {
  const navigate = useNavigate()
  const canQueue = useCanOpenQueue()
  const totals = COLUMNS.map((c) => rows.reduce((a, r) => a + r[c.key], 0))

  const openCell = (buyer: RosBuyerRow, col: (typeof COLUMNS)[number]) => {
    navigate(
      queueLink(
        { ...col.queueParams, buyerId: buyer.id, buyerName: buyer.name },
        { ...filters, buyerId: '' },
      ),
    )
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="text-sm text-gray-500 mb-3">По закупщикам</div>
      {rows.length === 0 ? (
        <div className="text-sm text-gray-400 py-6 text-center">В команде нет закупщиков</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3 font-medium">Закупщик</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} className="py-2 px-3 font-medium text-right whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-3 font-medium text-gray-900">{r.name}</td>
                  {COLUMNS.map((c) => (
                    <td key={c.key} className="py-1 px-1 text-right">
                      {canQueue ? (
                        <button
                          onClick={() => openCell(r, c)}
                          className="w-full px-2 py-1 rounded-lg tabular-nums text-gray-900 hover:bg-brand-50 hover:text-brand-700 transition-colors"
                          title={`Очередь: ${r.name} · ${c.label}`}
                        >
                          {r[c.key].toLocaleString('ru-RU')}
                        </button>
                      ) : (
                        <span className="block px-2 py-1 tabular-nums text-gray-900">
                          {r[c.key].toLocaleString('ru-RU')}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <td className="py-2 pr-3 text-xs font-semibold text-gray-500">Итого</td>
                {totals.map((t, i) => (
                  <td
                    key={COLUMNS[i]!.key}
                    className="py-2 px-3 text-right font-semibold text-gray-900 tabular-nums"
                  >
                    {t.toLocaleString('ru-RU')}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
